<template>
  <v-card flat>
    <v-card-title>Nutrient Targets</v-card-title>
    <v-card-text>
      <v-row align="center" dense>
        <v-col cols="12" md="3">
          <v-select
              label="Diet"
              v-model="dietId"
              :items="dietItems"
              item-text="name"
              item-value="id"
              hide-details
              outlined
              dense
          >
            <template v-slot:append-outer v-if="!readOnly">
              <v-btn icon small title="Add a new diet" @click="openDietDialog()">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
              <v-btn icon small title="Rename this diet" :disabled="!selectedDiet" @click="openDietDialog(selectedDiet)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small title="Delete this diet" :disabled="!selectedDiet" @click="deleteDiet">
                <v-icon>mdi-trash-can</v-icon>
              </v-btn>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
              label="Compare with"
              v-model="otherDietId"
              :items="otherDietItems"
              item-text="name"
              item-value="id"
              :disabled="!dietId"
              clearable
              hide-details
              outlined
              dense
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
              v-if="!readOnly"
              label="Add nutrient"
              :items="addableNutrients"
              item-text="label"
              item-value="nutrientnameid"
              v-model="nutrientToAdd"
              :disabled="!dietId"
              @change="addNutrient"
              hide-details
              outlined
              dense
          />
        </v-col>
        <v-col cols="12" md="3" class="text-right">
          <v-btn v-if="!readOnly" color="primary" :disabled="changedRows.length === 0" :loading="saving" @click="save">
            Save
            <template v-if="changedRows.length > 0">({{ changedRows.length }})</template>
            <v-icon right>mdi-content-save</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <p v-if="selectedDiet" class="text-caption mt-2 mb-0">
        {{ selectedDiet.name }} is the diet for the <b>{{ selectedDiet.stream || 'unassigned' }}</b> stream.
        <template v-if="otherDiet">
          Highlighted cells differ from {{ otherDiet.name }}. Use the arrows to copy a value across, then save.
        </template>
      </p>

      <v-alert v-if="loading" type="info" text dense class="mt-4">Loading targets...</v-alert>
      <v-alert v-else-if="!dietId" type="info" text dense class="mt-4">Select a diet to see its nutrient targets.</v-alert>

      <!-- one diet: edit its targets -->
      <v-simple-table v-else-if="!otherDietId" dense class="mt-4 targets-table">
        <thead>
        <tr>
          <th>Nutrient</th>
          <th>Symbol</th>
          <th>Target</th>
          <th>Upper limit</th>
          <th>Lower limit</th>
          <th v-if="!readOnly"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row of selectedDietRows" v-bind:key="row.nutrient_id" :class="{changed: isChanged(row)}">
          <td>{{ nutrientName(row.nutrient_id) }}</td>
          <td>{{ nutrientField(row.nutrient_id, 'nutrientsymbol') }}</td>
          <td v-for="field of fields" v-bind:key="field" class="value-cell">
            <NutrientTargetField
                v-model="row[field]"
                :unit="nutrientField(row.nutrient_id, 'unit')"
                :required="field === 'target_value'"
                :disabled="readOnly"
            />
          </td>
          <td v-if="!readOnly">
            <v-btn icon small title="Remove this nutrient target" @click="removeRow(row)">
              <v-icon small>mdi-trash-can</v-icon>
            </v-btn>
          </td>
        </tr>
        <tr v-if="selectedDietRows.length === 0">
          <td colspan="6" class="text-center">No nutrient targets yet. Use "Add nutrient" above.</td>
        </tr>
        </tbody>
      </v-simple-table>

      <!-- two diets side by side -->
      <v-simple-table v-else dense class="mt-4 targets-table compare-table">
        <thead>
        <tr>
          <th rowspan="2">Nutrient</th>
          <th rowspan="2">Symbol</th>
          <th v-for="field of fields" v-bind:key="field" colspan="3" class="text-center field-group">
            {{ fieldLabel(field) }}
          </th>
        </tr>
        <tr>
          <template v-for="field of fields">
            <th v-bind:key="`${field}-mine`" class="field-group">{{ selectedDiet.name }}</th>
            <th v-bind:key="`${field}-copy`"></th>
            <th v-bind:key="`${field}-other`">{{ otherDiet.name }}</th>
          </template>
        </tr>
        </thead>
        <tbody>
        <tr v-for="entry of comparedRows" v-bind:key="entry.nutrient_id">
          <td>{{ nutrientName(entry.nutrient_id) }}</td>
          <td>{{ nutrientField(entry.nutrient_id, 'nutrientsymbol') }}</td>
          <template v-for="field of fields">
            <td v-bind:key="`${field}-mine`"
                class="value-cell field-group"
                :class="{differs: entry.differences.includes(field), changed: entry.mine && isChanged(entry.mine)}">
              <NutrientTargetField
                  v-if="entry.mine"
                  v-model="entry.mine[field]"
                  :unit="nutrientField(entry.nutrient_id, 'unit')"
                  :required="field === 'target_value'"
                  :disabled="readOnly"
              />
              <span v-else class="text--disabled">missing</span>
            </td>
            <td v-bind:key="`${field}-copy`" class="copy-cell">
              <template v-if="!readOnly && entry.differences.includes(field)">
                <v-btn icon x-small :title="`copy ${otherDiet.name} value to ${selectedDiet.name}`"
                       :disabled="!entry.other"
                       @click="copyValue(entry, field, 'other', 'mine')">
                  <v-icon small>mdi-arrow-left</v-icon>
                </v-btn>
                <v-btn icon x-small :title="`copy ${selectedDiet.name} value to ${otherDiet.name}`"
                       :disabled="!entry.mine"
                       @click="copyValue(entry, field, 'mine', 'other')">
                  <v-icon small>mdi-arrow-right</v-icon>
                </v-btn>
              </template>
            </td>
            <td v-bind:key="`${field}-other`"
                class="value-cell"
                :class="{differs: entry.differences.includes(field), changed: entry.other && isChanged(entry.other)}">
              <NutrientTargetField
                  v-if="entry.other"
                  v-model="entry.other[field]"
                  :unit="nutrientField(entry.nutrient_id, 'unit')"
                  :required="field === 'target_value'"
                  :disabled="readOnly"
              />
              <span v-else class="text--disabled">missing</span>
            </td>
          </template>
        </tr>
        <tr v-if="comparedRows.length === 0">
          <td colspan="11" class="text-center">Neither diet has any nutrient targets.</td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>

    <v-dialog v-model="dietDialog.open" max-width="420">
      <v-card>
        <v-card-title>{{ dietDialog.id ? 'Rename diet' : 'New diet' }}</v-card-title>
        <v-card-text>
          <v-text-field label="Name" v-model="dietDialog.name" autofocus/>
          <v-combobox
              label="Stream"
              v-model="dietDialog.stream"
              :items="streamIds"
              hint="the app stream whose customers use these targets"
              persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dietDialog.open = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!dietDialog.name" :loading="dietDialog.saving" @click="saveDiet">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import api from '@/api';
import NutrientTargetField from '@/components/NutrientTargetField.vue';
import urlState from '@/router/urlState';
import {deepCopy} from '@/store/utils';
import {
  TARGET_FIELDS,
  changedTargets,
  compareRows,
  dietRows,
  indexByKey,
  newTargetRow,
  normaliseValue,
  targetChanged,
  targetKey,
} from '@/utils/nutrient-targets';

const FIELD_LABELS = {
  target_value: 'Target',
  upper_limit: 'Upper limit',
  lower_limit: 'Lower limit',
};

function parseDietId(value) {
  const id = Number(value);
  return Number.isNaN(id) || !value ? null : id;
}

export default {
  name: 'NutrientTargets',
  components: {NutrientTargetField},
  mixins: [urlState],
  props: {
    readOnly: {type: Boolean, default: false, required: false},
  },
  data() {
    return {
      fields: TARGET_FIELDS,
      diets: [],
      dietId: null,
      otherDietId: null,
      nutrientToAdd: null,
      // every diet's targets, edited in place; originalByKey holds what the api returned
      rows: [],
      originalByKey: {},
      loading: true,
      saving: false,
      dietDialog: {open: false, id: null, name: '', stream: null, saving: false},
    };
  },
  mounted() {
    this.syncToUrl({param: 'dietId', urlParam: 'diet', initFromRoute: true, parseCallback: parseDietId});
    this.syncToUrl({param: 'otherDietId', urlParam: 'compare', initFromRoute: true, parseCallback: parseDietId});
    return Promise.all([
      this.fetchNutrients(),
      this.fetchStreams(),
      this.loadDiets(),
      this.loadTargets(),
    ]);
  },
  beforeRouteLeave(to, from, next) {
    if (this.changedRows.length > 0 && !confirm('You have unsaved nutrient target changes. Leave without saving?')) {
      return next(false);
    }
    next();
  },
  watch: {
    dietId(id) {
      if (id === this.otherDietId) {
        this.otherDietId = null;
      }
    },
  },
  computed: {
    ...mapState(['nutrients', 'streams']),
    dietItems() {
      return [...this.diets].sort((a, b) => a.name.localeCompare(b.name));
    },
    otherDietItems() {
      return this.dietItems.filter(d => d.id !== this.dietId);
    },
    selectedDiet() {
      return this.diets.find(d => d.id === this.dietId) || null;
    },
    otherDiet() {
      return this.diets.find(d => d.id === this.otherDietId) || null;
    },
    streamIds() {
      return Object.keys(this.streams || {}).sort();
    },
    selectedDietRows() {
      return dietRows(this.rows, this.dietId);
    },
    comparedRows() {
      return compareRows(this.rows, this.dietId, this.otherDietId);
    },
    changedRows() {
      return changedTargets(this.rows, this.originalByKey);
    },
    addableNutrients() {
      const used = new Set(this.selectedDietRows.map(r => r.nutrient_id));
      return Object.values(this.nutrients || {})
          .filter(n => !used.has(n.nutrientnameid))
          .map(n => ({...n, label: `${n.nutrientname} (${n.nutrientsymbol}, ${n.unit})`}))
          .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  methods: {
    ...mapActions(['fetchNutrients', 'fetchStreams']),
    async loadDiets() {
      const {data} = await api.get('diets/');
      this.diets = data;
      // other pages read diets from the store; keep it in step with any edits made here
      const byId = data.reduce((acc, diet) => {
        acc[diet.id] = diet;
        return acc;
      }, {});
      this.$store.commit('SET_DIETS', {diets: byId});
    },
    async loadTargets() {
      this.loading = true;
      try {
        const {data} = await api.get('nutrients/targets/');
        this.rows = deepCopy(data);
        this.originalByKey = indexByKey(data);
      } finally {
        this.loading = false;
      }
    },
    nutrientField(nutrientId, field) {
      const nutrient = (this.nutrients || {})[nutrientId];
      return nutrient ? nutrient[field] : '';
    },
    nutrientName(nutrientId) {
      return this.nutrientField(nutrientId, 'nutrientname') || `nutrient #${nutrientId}`;
    },
    fieldLabel(field) {
      return FIELD_LABELS[field];
    },
    isChanged(row) {
      return targetChanged(row, this.originalByKey[targetKey(row.diet, row.nutrient_id)]);
    },
    addNutrient(nutrientId) {
      if (!nutrientId || !this.dietId) {
        return;
      }
      this.rows.push(newTargetRow(this.dietId, nutrientId));
      this.$nextTick(() => this.nutrientToAdd = null);
    },
    copyValue(entry, field, from, to) {
      if (!entry[from]) {
        return;
      }
      if (!entry[to]) {
        const dietId = to === 'mine' ? this.dietId : this.otherDietId;
        const row = newTargetRow(dietId, entry.nutrient_id);
        this.rows.push(row);
        entry[to] = row;
      }
      entry[to][field] = entry[from][field];
    },
    async removeRow(row) {
      const key = targetKey(row.diet, row.nutrient_id);
      const existsOnServer = !!this.originalByKey[key];
      if (existsOnServer) {
        const dietName = (this.diets.find(d => d.id === row.diet) || {}).name;
        if (!confirm(`Remove the ${this.nutrientName(row.nutrient_id)} target from ${dietName}?`)) {
          return;
        }
        try {
          await api.delete(`diets/targets/${row.diet}/${row.nutrient_id}`);
        } catch (e) {
          console.error('failed to delete target', e);
          return this.notify('Failed to remove target', 'error');
        }
        this.$delete(this.originalByKey, key);
      }
      this.rows = this.rows.filter(r => r !== row);
    },
    async save() {
      const changes = this.changedRows;
      const missing = changes.filter(r => normaliseValue(r.target_value) === null);
      if (missing.length > 0) {
        return this.notify(`Every nutrient needs a target value: ${missing.map(r => this.nutrientName(r.nutrient_id)).join(', ')}`, 'error');
      }
      this.saving = true;
      try {
        await api.post('diets/targets', {targets: changes});
        await this.loadTargets();
        this.notify(`Saved ${changes.length} nutrient target${changes.length === 1 ? '' : 's'}`, 'success');
      } catch (e) {
        console.error('failed to save targets', e);
        this.notify('Failed to save targets', 'error');
      } finally {
        this.saving = false;
      }
    },
    openDietDialog(diet = null) {
      this.dietDialog = {
        open: true,
        id: diet ? diet.id : null,
        name: diet ? diet.name : '',
        stream: diet ? diet.stream : null,
        saving: false,
      };
    },
    async saveDiet() {
      const {id, name, stream} = this.dietDialog;
      this.dietDialog.saving = true;
      try {
        if (id) {
          await api.put(`diets/${id}`, {name, stream});
        } else {
          const {data} = await api.post('diets/create', {name, stream});
          this.dietId = data.id;
        }
        await this.loadDiets();
        this.dietDialog.open = false;
      } catch (e) {
        console.error('failed to save diet', e);
        this.notify('Failed to save diet', 'error');
      } finally {
        this.dietDialog.saving = false;
      }
    },
    async deleteDiet() {
      const diet = this.selectedDiet;
      if (!diet) {
        return;
      }
      if (this.selectedDietRows.length > 0) {
        return this.notify(`Remove all of ${diet.name}'s nutrient targets before deleting it`, 'error');
      }
      if (!confirm(`Delete the diet ${diet.name}?`)) {
        return;
      }
      try {
        await api.delete(`diets/${diet.id}`);
        this.dietId = null;
        await this.loadDiets();
      } catch (e) {
        console.error('failed to delete diet', e);
        this.notify('Failed to delete diet. It may still be used by meals.', 'error');
      }
    },
    notify(message, color) {
      this.$store.dispatch('setSnackbar', {message, color});
    },
  },
};
</script>

<style scoped>
.targets-table td.value-cell {
  width: 9em;
}

.targets-table tr.changed td:first-child,
.targets-table td.changed {
  box-shadow: inset 3px 0 0 var(--v-warning-base);
}

.compare-table td.value-cell {
  width: 7.5em;
}

.compare-table td.differs {
  background: rgba(255, 193, 7, 0.12);
}

.compare-table .field-group {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.compare-table td.copy-cell {
  white-space: nowrap;
  width: 1%;
  padding: 0;
}
</style>
